import { useRef, useState, type DragEvent, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import classes from './FileDropZone.module.scss';

type FileDropZoneProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onDrop' | 'onDragOver' | 'onDragEnter' | 'onDragLeave'
> & {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: string;
  children: ReactNode;
};

const fileMatchesAccept = (file: File, accept: string): boolean => {
  const parts = accept
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  if (!parts.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return parts.some((part) => {
    if (part.startsWith('.')) return name.endsWith(part);
    if (part.endsWith('/*')) return type.startsWith(part.slice(0, -1));
    return type === part;
  });
};

export const FileDropZone = ({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  className,
  hint = 'Отпустите файл, чтобы загрузить',
  children,
  ...rest
}: FileDropZoneProps) => {
  const [active, setActive] = useState(false);
  const depth = useRef(0);

  const hasFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !hasFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setActive(true);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled || !hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setActive(false);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    depth.current = 0;
    setActive(false);
    if (disabled) return;
    const dropped = Array.from(event.dataTransfer.files || []);
    const matched = accept ? dropped.filter((file) => fileMatchesAccept(file, accept)) : dropped;
    const next = multiple ? matched : matched.slice(0, 1);
    if (next.length) onFiles(next);
  };

  return (
    <div
      {...rest}
      className={clsx(classes.zone, active && classes.active, className)}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
      {active ? <div className={classes.overlay}>{hint}</div> : null}
    </div>
  );
};
